import * as Error from '../../utils/error';
import * as DataViewApis from '../../apis/dataView';
import * as DataViewErrors from '../../errors/dataView';
import * as CurrentActions from '../current';
import * as TeamActions from '../team';
import * as LoadingActions from '../loading';
import * as GroupsActions from '../groups';
import * as ErrorsActions from '../errors';
import * as CollaboratorsSelectors from '../../selectors/collaborators';
import * as GroupsSelectors from '../../selectors/groups';
import * as FilesSelectors from '../../selectors/files';
import * as FilerrsSelectors from '../../selectors/filerrs';
import * as CurrentsSelectors from '../../selectors/current';
import * as ErrorsSelectors from '../../selectors/errors';
import * as SettingsSelectors from '../../selectors/settings';
import * as SettingsActions from '../settings';
import { initializeGroup } from './initializeGroup';
import { setDataviewModels, setDataviewCurrents } from './updateDataview';

export const get = (payload, pdfNoCollaboration = false) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                DataViewErrors.DATAVIEW_ACTIONS_GET_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(async() => {
        const dataview = await DataViewApis.fetch(payload.filerrIds, payload.userId, payload.collaboratorId, pdfNoCollaboration);
        const { showPdfViewer } = dataview;
        dispatch(SettingsActions.setShowPdfViewer(showPdfViewer));
        if (!pdfNoCollaboration) {
            dispatch(setDataviewModels(dataview));
            assertRequiredGroupTypeExists(getState());
            dispatch(setDataviewCurrents(payload));
            await dispatch(GroupsActions.updateLastSeen(payload.groupType));
        }
    }).catch(async(error) => {

        switch (error.code) {
            case DataViewErrors.DATAVIEW_APIS_FETCH_NOT_FOUND: {
                const requiredGroupType = getRequiredGroupType(getState());

                return dispatch(
                    initializeGroup(requiredGroupType, payload.filerrIds, payload.recipientIds)
                ).then(
                    () => dispatch(get(payload))
                );
            }

            case DataViewErrors.DATAVIEW_APIS_FETCH_COLLABORATOR_REMOVED: {
                return dispatch(ErrorsActions.setRemovedCollaboratorError(true));
            }

            default: {
                throw error;
            }
        }
    }).catch((error) => {
        if (!error.code) {
            error.code = DataViewErrors.DATAVIEW_ACTIONS_GET_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });

function getRequiredGroupType(state) {
    const requiredGroupType = SettingsSelectors.getRequiredGroupType(state);

    if (!requiredGroupType) {
        throw Error.create(
            'Falsy requiredGroupType. Cannot initialize dataview.',
            DataViewErrors.DATAVIEW_ACTIONS_GET_FALSY_REQUIRED_GROUP_TYPE
        );
    }
    return requiredGroupType;
}

function assertRequiredGroupTypeExists(state) {
    const requiredGroup = SettingsSelectors.getRequiredGroupType(state);

    if (!GroupsSelectors.isGroupExist(state, requiredGroup)) {
        throw Error.create(`required group (${requiredGroup}) not found`, DataViewErrors.DATAVIEW_APIS_FETCH_NOT_FOUND);
    }
}

export const init = (payload) => (dispatch, getState) =>
    Promise.resolve().then(() => {
        if (!payload) {
            throw Error.create(
                `Payload is falsy: ${payload}`,
                DataViewErrors.DATAVIEW_ACTIONS_INIT_FALSY_PAYLOAD,
                payload
            );
        }
    }).then(() => {
        dispatch(LoadingActions.setDataViewLoading(true));
    }).then(() => {
        if (payload.withCollaboration) {
            return dispatch(get(payload));
        }
        dispatch(get(payload, true));
        dispatch(CurrentActions.setCurrentFilerrId(payload.currentFilerrId));
    }).then(() => {
        if (SettingsSelectors.isTeamCollaboration(getState())) {
            return dispatch(TeamActions.fetch(payload));
        }
    }).then(() => {
        if (ErrorsSelectors.isRemovedCollaboratorError(getState())) {
            return;
        }

        if (FilerrsSelectors.isEmpty(getState())) {
            throw Error.create(
                'No filerrs in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_FILERRS,
                payload
            );
        }

        if (!CurrentsSelectors.getCurrentFilerrId(getState())) {
            throw Error.create(
                'No current filerr id in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_CURRENT_FILERR_ID,
                payload
            );
        }

        if (!payload.withCollaboration) {
            return;
        }

        if (GroupsSelectors.isEmpty(getState())) {
            throw Error.create(
                'No groups in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_GROUPS,
                payload
            );
        }

        if (CollaboratorsSelectors.isEmpty(getState())) {
            throw Error.create(
                'No collaborators in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_COLLABORATORS,
                payload
            );
        }

        if (FilesSelectors.isEmpty(getState())) {
            throw Error.create(
                'No files in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_FILES,
                payload
            );
        }

        if (!CurrentsSelectors.getCurrentGroupId(getState())) {
            throw Error.create(
                'No current group id in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_CURRENT_GROUP_ID,
                payload
            );
        }

        if (!CurrentsSelectors.getCurrentCollaboratorId(getState())) {
            throw Error.create(
                'No current collaborator id in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_CURRENT_COLLABORATOR_ID,
                payload
            );
        }

        if (!CurrentsSelectors.getCurrentFileId(getState())) {
            throw Error.create(
                'No current file id in store!',
                DataViewErrors.DATAVIEW_ACTIONS_INIT_MISS_CURRENT_FILE_ID,
                payload
            );
        }
    }).then(() => {
        dispatch(LoadingActions.setDataViewLoading(false));
    }).catch((error) => {
        dispatch(LoadingActions.setDataViewLoading(false));

        throw error;
    }).catch((error) => {
        if (!error.code) {
            error.code = DataViewErrors.DATAVIEW_ACTIONS_INIT_UNEXPECTED;
            error.details = { payload: JSON.stringify(payload) };
        }

        throw error;
    });
